import type { EmployerSnapshot } from '@klukka/client';
import {
  fmtDay,
  fmtH,
  fmtTs,
  type AdminLang,
  type EditLogEntry,
  type LeaveRequest,
  type RequestStatus,
  type TimesheetStatus,
} from '@klukka/core';
import { Trail } from '@klukka/ui';
import { AdminChip, Panel, type Tr } from '../widgets';

/**
 * Everything waiting on a decision, plus the two audit logs that make those
 * decisions defensible later: overtime confirmations and backdated hour edits.
 */
export function ApprovalsTab({ snap, tr, lang, onRequest, onTimesheet }: {
  snap: EmployerSnapshot;
  tr: Tr;
  lang: AdminLang;
  onRequest: (id: string, status: RequestStatus) => void;
  onTimesheet: (id: string, status: TimesheetStatus) => void;
}) {
  const nameOf = (empId: string) => snap.employees.find((e) => e.id === empId)?.name ?? empId;

  // A shift the system closed records no hours; the row stays flagged until
  // somebody gives that day hours.
  const needsHours = (e: EditLogEntry) =>
    e.who === 'Klukka' && e.oldH === e.newH &&
    (snap.employees.find((x) => x.id === e.empId)?.worklog[e.date]?.h ?? 0) === e.newH;

  const typeLabel = (r: LeaveRequest) =>
    r.type === 'sick' ? tr('sick') : r.type === 'leave' ? tr('leaveLbl') : r.type === 'open' ? tr('openReq') : tr('vacationLbl');

  const spanOf = (r: LeaveRequest) => {
    const time = (x: string, skip: string) => (x && x !== skip ? ` ${x}` : '');
    return `${fmtDay(r.from)}${time(r.fromTime, '00:00')} → ${fmtDay(r.to)}${time(r.toTime, '23:59')}`;
  };

  return (
    <div>
      <Panel title={tr('worklogs')}>
        {snap.timesheets.length === 0 ? <div className="dimtx" style={{ fontSize: 13 }}>{tr('empty')}</div> : null}
        {snap.timesheets.map((w) => (
          <div className="ap-row" key={w.id}>
            <div>
              <b>{nameOf(w.empId)}</b> · {w.label} · <span className="mono">{fmtH(w.hours)} klst</span>
              <Trail trail={w.trail} />
            </div>
            <div className="ap-act">
              <AdminChip status={w.status} lang={lang} />
              {w.status === 'submitted' ? (
                <span>
                  <button className="btn-sm ok" onClick={() => onTimesheet(w.id, 'approved')}>{tr('approve')}</button>
                  <button className="btn-sm no" onClick={() => onTimesheet(w.id, 'rejected')}>{tr('reject')}</button>
                </span>
              ) : null}
            </div>
          </div>
        ))}
      </Panel>

      <Panel title={tr('requests')}>
        {snap.requests.length === 0 ? <div className="dimtx" style={{ fontSize: 13 }}>{tr('empty')}</div> : null}
        {snap.requests.map((r) => (
          <div className="ap-row" key={r.id}>
            <div>
              <b>{nameOf(r.empId)}</b> · {typeLabel(r)}
              {r.type !== 'open' ? <span className="mono" style={{ fontSize: 12 }}> · {spanOf(r)}</span> : null}
              {r.note ? <span className="dimtx"> · {r.note}</span> : null}
              <Trail trail={r.trail} />
            </div>
            <div className="ap-act">
              <AdminChip status={r.status} lang={lang} />
              {r.status === 'pending' ? (
                <span>
                  <button className="btn-sm ok" onClick={() => onRequest(r.id, 'approved')}>{tr('approve')}</button>
                  <button className="btn-sm no" onClick={() => onRequest(r.id, 'rejected')}>{tr('reject')}</button>
                </span>
              ) : null}
            </div>
          </div>
        ))}
      </Panel>

      <Panel title={tr('otConfirms')}>
        {snap.overtimeConfirmations.length === 0 ? <div className="dimtx" style={{ fontSize: 13 }}>{tr('empty')}</div> : null}
        {snap.overtimeConfirmations.map((c) => (
          <div className="ap-row" key={c.id}>
            <div>
              <b>{c.who}</b> · <span className="mono">{fmtH(c.hours)} klst</span>
              <span className="dimtx"> · {tr('otConfNote')}</span>
            </div>
            <span className="dimtx mono" style={{ fontSize: 12 }}>{fmtTs(c.ts)}</span>
          </div>
        ))}
      </Panel>

      <Panel title={tr('editLog')}>
        {snap.editLog.length === 0 ? <div className="dimtx" style={{ fontSize: 13 }}>{tr('empty')}</div> : null}
        {snap.editLog.map((e) => (
          <div className="ap-row" key={e.id}>
            <div>
              <b>{nameOf(e.empId)}</b> · <span className="mono">{fmtDay(e.date)}</span> · {tr('was')}{' '}
              <span className="mono">{fmtH(e.oldH)}</span> → {tr('now')} <b className="mono">{fmtH(e.newH)} klst</b>
              <span className="dimtx"> · {e.who}{e.note ? ` — ${e.note}` : ''}</span>
              {needsHours(e) ? (
                <span className="chip" style={{ borderColor: 'var(--warn)', color: 'var(--warn)', marginLeft: 6 }}>
                  {tr('hoursNeeded')}
                </span>
              ) : null}
            </div>
            <span className="dimtx mono" style={{ fontSize: 12 }}>{fmtTs(e.ts)}</span>
          </div>
        ))}
      </Panel>
    </div>
  );
}
